import { groupBy } from "@senciamatch/frontend/libs/array";
import type { Building } from "@senciamatch/shared/models/building";
import type { BuildingShift } from "@senciamatch/shared/models/buildingShift";
import { createBuildingShift } from "@senciamatch/shared/models/buildingShift";
import { createBuildingShiftGroup } from "@senciamatch/shared/models/buildingShiftGroup";
import type { BuildingShiftRow } from "@senciamatch/shared/models/buildingShiftRow";
import { createBuildingShiftRow } from "@senciamatch/shared/models/buildingShiftRow";
import type { Staff } from "@senciamatch/shared/models/staff";
import type { StaffShift } from "@senciamatch/shared/models/staffShift";
import { createStaffShift } from "@senciamatch/shared/models/staffShift";
import type { WorkShift } from "@senciamatch/shared/models/workShift";
import { createWorkShift } from "@senciamatch/shared/models/workShift";
import { createWorkShiftGroup } from "@senciamatch/shared/models/workShiftGroup";
import type { CellData } from "./types";
import {
  excelSerialDateToYYYYMM,
  getCasNaviIdAndName,
  getCasNaviIdAndNameFromValue,
  getLastDayOfMonth,
  nextCell,
} from "./utils";

type ROW_TYPE = "building" | "staff" | "unknown";
const BUILDING_COL = "B";
const RAW_START = 12;
const STAFF_ID_COL = "Q";

const getRowType = (columnB: CellData[string], columnQ: CellData[string]): ROW_TYPE => {
  if (columnB.comment) return "building";
  // Also check if columnB value contains building info format
  if (columnB.value && /^\d+：/.test(columnB.value.toString())) return "building";
  if (columnQ.value) return "staff";
  return "unknown";
};

const buildBuildingShiftGroup = (cellData: CellData) => {
  const dateCell = excelSerialDateToYYYYMM(Number(cellData.B6.value));
  const year = Number(dateCell.substring(0, 4));
  const month = Number(dateCell.substring(4, 6));
  return createBuildingShiftGroup({
    year,
    month,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

const buildWorkShiftGroup = (
  cellData: CellData,
  staffShiftGroupId: string | undefined,
  buildingShiftGroupId: string,
) => {
  const dateCell = excelSerialDateToYYYYMM(Number(cellData.B6.value));
  const year = Number(dateCell.substring(0, 4));
  const month = Number(dateCell.substring(4, 6));

  // staffShiftGroupIdが未定義の場合は、エラーをスローする
  if (!staffShiftGroupId) {
    throw new Error(
      "スタッフシフトグループIDが設定されていません。スタッフシフトが正しく登録されているか確認してください。",
    );
  }

  return createWorkShiftGroup({
    year,
    month,
    name: `${year}年${month}月 マッチング`,
    staffShiftGroupId,
    buildingShiftGroupId,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

interface BuildBuildingShiftRowParams {
  nameCell: CellData[string];
  shiftTimeCell: CellData[string];
  buildingShiftGroupId: string;
  buildingId: string;
  shiftTypeName?: string | null;
}

const buildBuildingShiftRow = ({
  nameCell,
  shiftTimeCell,
  buildingShiftGroupId,
  buildingId,
  shiftTypeName,
}: BuildBuildingShiftRowParams) => {
  let name = nameCell.value?.toString();

  // If shiftTypeName is provided (from new format), use it as the name
  if (shiftTypeName) {
    name = shiftTypeName;
  }

  const shiftTime = shiftTimeCell.value;
  if (!name || !shiftTime) {
    throw new Error("name or shiftTime is null");
  }

  const [startHour, startMinute, endHour, endMinute] = shiftTime.toString().split(/-|:/);
  return createBuildingShiftRow({
    buildingId,
    name,
    buildingShiftGroupId,
    startHour: Number(startHour),
    startMinute: Number(startMinute),
    endHour: Number(endHour),
    endMinute: Number(endMinute),
  });
};

/**
 * createShiftBatch用のデータ構造を作成するパーサー
 */
export const buildShiftBatchData = (
  cellData: CellData,
  buildings: Building[],
  staffs: Staff[],
  staffShifts: StaffShift[],
) => {
  const buildingGroupByName = groupBy(buildings, (b) => b.name);
  const buildingGroupByNayose = groupBy(
    buildings.filter((b) => b.nameNayose),
    (b) => b.nameNayose ?? "undefined",
  );
  const errors: string[] = [];
  const buildingShiftRows: BuildingShiftRow[] = [];
  const buildingShifts: BuildingShift[] = [];
  const workShifts: WorkShift[] = [];
  const addStaffShifts: StaffShift[] = [];

  const staffShiftGroupId = staffShifts[0]?.staffShiftGroupId;
  const staffGroupByStaffId = groupBy(staffs, (s) => s.id);
  const staffShiftGroupByStaffId = groupBy(staffShifts, (s) => s.staffId);
  const buildingShiftGroup = buildBuildingShiftGroup(cellData);
  const workShiftGroup = buildWorkShiftGroup(cellData, staffShiftGroupId, buildingShiftGroup.id);
  const lastDayOfMonth = getLastDayOfMonth(cellData);

  let raw = RAW_START;
  let buildingShiftRow: BuildingShiftRow | null = null;

  // 行のループ
  while (true) {
    const rowType = getRowType(cellData[`${BUILDING_COL}${raw}`], cellData[`${STAFF_ID_COL}${raw}`]);

    if (rowType === "unknown") break;
    if (rowType === "building") {
      // Try to get building info from comment first (old format)
      let [casNaviId, buildingName] = getCasNaviIdAndName(cellData[`${BUILDING_COL}${raw}`].comment ?? "");
      let shiftTypeName = null;

      // If not found in comment, try to get from cell value (new format)
      if (!casNaviId || !buildingName) {
        const cellValue = cellData[`${BUILDING_COL}${raw}`].value?.toString() ?? "";
        const result = getCasNaviIdAndNameFromValue(cellValue);
        casNaviId = result[0];
        buildingName = result[1];
        shiftTypeName = result[2]; // This captures shift type like "【早番】"
      }

      if (!casNaviId || !buildingName) {
        console.warn("casNaviId or buildingName is null", cellData[`${BUILDING_COL}${raw}`], raw);
        raw++;
        continue;
      }

      // 物件を検索
      let building = buildingGroupByName[buildingName] ? buildingGroupByName[buildingName][0] : null;
      if (!building) {
        building = buildingGroupByNayose[buildingName] ? buildingGroupByNayose[buildingName][0] : null;
      }

      if (!building) {
        console.warn("building is null", buildingName);
        const errorStr = `該当の物件が見つかりません: ${buildingName}`;
        if (!errors.includes(errorStr)) {
          errors.push(errorStr);
        }
        buildingShiftRow = null;
      } else {
        buildingShiftRow = buildBuildingShiftRow({
          nameCell: cellData[`${BUILDING_COL}${raw}`],
          shiftTimeCell: cellData[`${STAFF_ID_COL}${raw}`],
          buildingShiftGroupId: buildingShiftGroup.id,
          buildingId: building.id,
          shiftTypeName: shiftTypeName,
        });
        buildingShiftRows.push(buildingShiftRow);

        let shiftCell = `X${raw}`;
        for (let day = 1; day <= lastDayOfMonth; day++) {
          const cellValue = cellData[shiftCell]?.value;
          console.log("cellValue", cellValue);
          if (cellValue != null) {
            console.log("cellValue is not null");
            // 一旦空のstaffIdでbuildingShiftを作成
            buildingShifts.push(
              createBuildingShift({
                buildingShiftRowId: buildingShiftRow.id,
                year: buildingShiftGroup.year,
                month: buildingShiftGroup.month,
                day,
                staffCount: Number(cellValue),
                buildingId: building.id,
              }),
            );
          }
          shiftCell = nextCell(shiftCell) ?? "";
        }
      }
    }
    if (rowType === "staff" && buildingShiftRow != null) {
      const staffId = cellData[`${STAFF_ID_COL}${raw}`].value?.toString();
      const staff = staffId ? staffGroupByStaffId[staffId as Staff["id"]]?.[0] : null;

      if (!staff) {
        console.warn("staff is null", staffId);
      } else {
        let shiftCell = `X${raw}`;
        for (let day = 1; day <= lastDayOfMonth; day++) {
          const cellValue = cellData[shiftCell]?.value;
          let staffShift = (staffShiftGroupByStaffId[staffId as Staff["id"]] ?? []).find((s) => s.day === day);
          if (!staffShift) {
            console.warn("staffShift is null", staffId, day);
            staffShift = createStaffShift({
              staffId,
              day,
              staffShiftGroupId,
              year: buildingShiftGroup.year,
              month: buildingShiftGroup.month,
              shiftSymbol: "-",
            });
            addStaffShifts.push(staffShift);
            // ローカルの検索用配列にも追加
            if (!staffShiftGroupByStaffId[staffId as Staff["id"]]) {
              staffShiftGroupByStaffId[staffId as Staff["id"]] = [];
            }
            staffShiftGroupByStaffId[staffId as Staff["id"]].push(staffShift);
          }

          if (cellValue) {
            let buildingShift = buildingShifts.find(
              (bs) => bs.buildingShiftRowId === buildingShiftRow?.id && bs.day === day,
            );
            if (!buildingShift) {
              buildingShift = createBuildingShift({
                buildingShiftRowId: buildingShiftRow.id,
                year: buildingShiftGroup.year,
                month: buildingShiftGroup.month,
                day,
                staffCount: 1,
                buildingId: buildingShiftRow.buildingId,
              });
              buildingShifts.push(buildingShift);
            }
            workShifts.push(
              createWorkShift({
                workShiftGroupId: workShiftGroup.id,
                staffId: staff.id,
                staffShiftId: staffShift.id,
                buildingId: buildingShiftRow.buildingId,
                buildingShiftId: buildingShift.id,
                buildingShiftRowId: buildingShiftRow.id,
                year: buildingShiftGroup.year,
                month: buildingShiftGroup.month,
                day,
                startHour: buildingShiftRow.startHour,
                startMinute: buildingShiftRow.startMinute,
                endHour: buildingShiftRow.endHour,
                endMinute: buildingShiftRow.endMinute,
                status: "Confirmed",
                isFirstShift: false,
                decidedLogic: "ExcelImport",
              }),
            );
          }
          shiftCell = nextCell(shiftCell) ?? "";
        }
      }
    }

    raw++;
  }

  return {
    buildingShiftGroup,
    buildingShiftRows,
    buildingShifts,
    workShiftGroup,
    addStaffShifts,
    workShifts,
    errors,
  };
};
